import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Select,
  MenuItem,
  InputBase,
  IconButton,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const SEARCH_ATTRIBUTES = [
  { key: "po_code", label: "Order Code", placeholder: "Search by Order Code" },
  { key: "vendor_name", label: "Vendor Name", placeholder: "Search by Vendor Name" },
  { key: "invoice_number", label: "Invoice Number", placeholder: "Search by Invoice Number" },
];

function AttributeSearchInput({ value, selectedAttribute, onSearch, onClear, attributes }) {
  const attrList = useMemo(() => attributes || SEARCH_ATTRIBUTES, [attributes]);
  const [inputVal, setInputVal] = useState(value || "");
  const [attribute, setAttribute] = useState(
    selectedAttribute || attrList[0].key
  );
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputVal(value || "");
  }, [value]);

  useEffect(() => {
    setAttribute(selectedAttribute || attrList[0].key);
  }, [selectedAttribute, attrList]);

  const currentAttr =
    attrList.find((a) => a.key === attribute) || attrList[0];

  const handleSearch = () => {
    if (inputVal.trim()) {
      onSearch(attribute, inputVal.trim());
    }
  };

  const handleClear = () => {
    setInputVal("");
    onClear();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1px solid",
        borderColor: focused ? "#1565c0" : "#e0e0e0",
        borderWidth: focused ? 2 : 1,
        borderRadius: "4px",
        backgroundColor: "#fff",
        transition: "border-color 0.2s, border-width 0.2s",
        overflow: "hidden",
        height: 36,
        minWidth: { xs: 0, md: 420 },
        width: { xs: "100%", md: "auto" },
        "&:hover": {
          borderColor: focused ? "#1565c0" : "#bdbdbd",
        },
      }}
    >
      <Select
        value={attribute}
        onChange={(e) => {
          setAttribute(e.target.value);
          if (inputVal.trim()) {
            setInputVal("");
            onClear();
          }
          inputRef.current?.focus();
        }}
        variant="standard"
        disableUnderline
        IconComponent={ExpandMoreIcon}
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: "#333",
          backgroundColor: "#fff",
          height: "100%",
          px: 1.5,
          minWidth: 140,
          "& .MuiSelect-select": {
            py: 0,
            display: "flex",
            alignItems: "center",
            height: "100%",
            backgroundColor: "transparent !important",
          },
          "& .MuiSelect-icon": {
            color: "#666",
            right: 4,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: "4px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              mt: 0.5,
              "& .MuiMenuItem-root": {
                fontSize: 14,
                fontWeight: 500,
                py: 1.2,
                px: 2,
                "&.Mui-selected": {
                  backgroundColor: "rgba(21, 101, 192, 0.08)",
                  fontWeight: 700,
                },
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              },
            },
          },
        }}
      >
        {attrList.map((attr) => (
          <MenuItem key={attr.key} value={attr.key}>
            {attr.label}
          </MenuItem>
        ))}
      </Select>

      <Divider orientation="vertical" flexItem sx={{ borderColor: "#e0e0e0", my: 0.75 }} />

      <InputBase
        inputRef={inputRef}
        placeholder={currentAttr.placeholder}
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSearch();
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        sx={{
          flex: 1,
          fontSize: 14,
          px: 1.5,
          height: "100%",
          color: "#333",
        }}
        inputProps={{ "data-testid": "searchInput" }}
      />

      {inputVal && (
        <IconButton size="small" onClick={handleClear} sx={{ color: "#999", mr: 0.25 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      <IconButton
        size="small"
        onClick={handleSearch}
        sx={{
          color: inputVal.trim() ? "#757575" : "#bdbdbd",
          mr: 0.5,
          "&:hover": { color: inputVal.trim() ? "#1565c0" : "#bdbdbd" },
        }}
      >
        <SearchIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export { SEARCH_ATTRIBUTES };
export default AttributeSearchInput;
